/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BACKUP_FILES_PAGE_DATE_FILTER_FIX
 * Date:         2026-02-02
 * Modified By:  Cursor AI
 *
 * Description:
 * BUGFIX: Changed date dropdown default from "latest date" to "All dates"
 * to show all available backup files instead of hiding older/newer backups.
 *
 * Root Cause:
 * - Original code auto-selected latest date (line 117: setSelectedDate(nextDates[0]))
 * - This filtered the table to show only files from that specific date
 * - User couldn't see backups from other dates without changing dropdown
 *
 * Fix Applied:
 * - Commented out auto-selection code (lines 116-118)
 * - Date dropdown now defaults to "All dates"
 * - Table shows ALL backup files from ALL dates by default
 * - User can still filter by selecting a specific date manually
 *
 * File Size Check:
 * - Current: 366 lines
 * - Modified: ~370 lines (+4 comment lines)
 * - Projected: 370 lines
 * - Decision: Proceed (under 400 line limit)
 *
 * Related Files:
 * - client/src/services/schedulerApi.ts (no changes needed)
 * - server/controllers/admin/backupRestoreController.js (no changes needed)
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_BACKUP_FILES_PAGE_001
 * Date:         2026-01-31
 * Modified By:  Cursor AI
 *
 * Description:
 * Admin “Backup files” screen that lists available backup archives (indexed from R2 into MongoDB)
 * and allows triggering a restore for all collections or a selected set of collections.
 *
 * File Size Check:
 * - Current: 0 lines (new file)
 * - Adding: ~190 lines
 * - Projected: ~190 lines
 * - Decision: Proceed
 *
 * Related Files:
 * - client/src/services/schedulerApi.ts
 * - server/controllers/admin/backupRestoreController.js
 * - server/routes/admin/scheduler.js
 */

import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AdminButton from "../../../components/admin/AdminButton";
import schedulerApi from "../../../services/schedulerApi";
import "./BackupFilesPage.scss";

type Message = { type: "success" | "error"; text: string };

type RestoreMode = "all" | "selected";

type BackupFile = {
  _id: string;
  environment: "dev" | "test" | "prod";
  file_name: string;
  file_size: number;
  last_modified: string;
  discovered_at: string;
  deleted_at?: string | null;
};

const parseBackupFileName = (fileName: string) => {
  // Expected: backup-{env}-{YYYY-MM-DD-HHmmss}.tar.gz
  const m = /^backup-(dev|test|prod)-(\d{4}-\d{2}-\d{2})-(\d{6})\.tar\.gz$/.exec(
    fileName
  );
  if (!m) return null;
  const [, env, date, hhmmss] = m;
  const time = `${hhmmss.slice(0, 2)}:${hhmmss.slice(2, 4)}:${hhmmss.slice(4, 6)}`;
  return { env: env as BackupFile["environment"], date, time };
};

const formatBytes = (bytes: number) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

const BackupFilesPage: React.FC = () => {
  const navigate = useNavigate();

  const [files, setFiles] = useState<BackupFile[]>([]);
  const [collections, setCollections] = useState<string[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [message, setMessage] = useState<Message | null>(null);

  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [restoreMode, setRestoreMode] = useState<RestoreMode>("all");
  const [selectedCollections, setSelectedCollections] = useState<Record<string, boolean>>(
    {}
  );
  const [triggeringRestore, setTriggeringRestore] = useState(false);

  const parsedFiles = useMemo(() => {
    return files
      .map((f) => ({ ...f, parsed: parseBackupFileName(f.file_name) }))
      .filter((f) => !!f.parsed)
      .sort((a, b) => (a.file_name < b.file_name ? 1 : -1));
  }, [files]);

  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    parsedFiles.forEach((f) => dates.add(f.parsed!.date));
    return Array.from(dates).sort((a, b) => (a < b ? 1 : -1));
  }, [parsedFiles]);

  const filesForSelectedDate = useMemo(() => {
    if (!selectedDate) return parsedFiles;
    return parsedFiles.filter((f) => f.parsed!.date === selectedDate);
  }, [parsedFiles, selectedDate]);

  const loadBackupFiles = async () => {
    try {
      setLoadingFiles(true);
      setMessage(null);
      const resp = await schedulerApi.getBackupFiles({ limit: 500 });
      setFiles(resp.data.files || []);

      // [2026-01-31] UX: Default to latest date and latest file in that date
      const nextParsed = (resp.data.files || [])
        .map((f: BackupFile) => ({ ...f, parsed: parseBackupFileName(f.file_name) }))
        .filter((f: any) => !!f.parsed)
        .sort((a: any, b: any) => (a.file_name < b.file_name ? 1 : -1));

      const _nextDates = Array.from<string>(
        new Set<string>(nextParsed.map((f: any) => f.parsed.date as string))
      ).sort((a, b) => (a < b ? 1 : -1));
      void _nextDates; // keep var for (commented) defaulting; avoid unused-var lint

      // [2026-02-02] BUGFIX: Don't auto-select date - show all dates by default
      // This prevents hiding backups from other dates. User can manually filter if desired.
      // if (!selectedDate && _nextDates.length > 0) {
      //   setSelectedDate(_nextDates[0]);
      // }
      if (!selectedFileName && nextParsed.length > 0) {
        setSelectedFileName(nextParsed[0].file_name);
      }
    } catch (err: any) {
      setMessage({ type: "error", text: err.message || "Failed to load backup files" });
    } finally {
      setLoadingFiles(false);
    }
  };

  const loadCollections = async () => {
    try {
      setLoadingCollections(true);
      const resp = await schedulerApi.getDbCollections();
      setCollections(resp.data.collections || []);
    } catch (err: any) {
      setMessage({ type: "error", text: err.message || "Failed to load collections" });
    } finally {
      setLoadingCollections(false);
    }
  };

  useEffect(() => {
    loadBackupFiles();
    loadCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleCollection = (name: string) => {
    setSelectedCollections((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleTriggerRestore = async () => {
    try {
      setTriggeringRestore(true);
      setMessage(null);

      if (!selectedFileName) {
        setMessage({ type: "error", text: "Please select a backup file to restore." });
        return;
      }

      const selected = Object.keys(selectedCollections).filter((c) => selectedCollections[c]);
      if (restoreMode === "selected" && selected.length === 0) {
        setMessage({
          type: "error",
          text: "Please select at least one collection (or choose All collections).",
        });
        return;
      }

      await schedulerApi.triggerRestore({
        backup_file_name: selectedFileName,
        collections: restoreMode === "all" ? null : selected,
      });

      setMessage({
        type: "success",
        text:
          "Restore job scheduled. It will run in the scheduler queue. Pre-restore collections will be kept for rollback.",
      });
    } catch (err: any) {
      setMessage({ type: "error", text: err.message || "Failed to schedule restore" });
    } finally {
      setTriggeringRestore(false);
    }
  };

  return (
    <div className="admin-backup-page">
      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
        <div>
          <h2>Backup Files</h2>
          <p className="text-muted mb-4">Select a backup file and schedule a restore</p>
        </div>
        <div className="d-flex gap-2">
          <AdminButton intent="secondary" onClick={() => navigate("/admin/scheduler/backup")}>
            Back
          </AdminButton>
          <AdminButton intent="secondary" onClick={loadBackupFiles} disabled={loadingFiles}>
            {loadingFiles ? "Refreshing..." : "Refresh"}
          </AdminButton>
        </div>
      </div>

      {message && (
        <Alert
          variant={message.type === "success" ? "success" : "danger"}
          dismissible
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Row className="g-3">
        <Col lg={8}>
          <Card>
            <Card.Body>
              <Card.Title>Available Backups (Cloudflare R2)</Card.Title>

              {loadingFiles ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : parsedFiles.length === 0 ? (
                <Alert variant="info" className="mb-0">
                  No backup files found yet. If backups exist in R2, wait for the next sync.
                </Alert>
              ) : (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Choose date</Form.Label>
                    <Form.Select
                      value={selectedDate}
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setSelectedFileName("");
                      }}
                    >
                      <option value="">All dates</option>
                      {availableDates.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Table striped bordered hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Env</th>
                        <th>Size</th>
                        <th>Last Modified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filesForSelectedDate.map((f) => (
                        <tr key={f._id || f.file_name}>
                          <td className="backup-files-select-col">
                            <Form.Check
                              type="radio"
                              name="backupFile"
                              checked={selectedFileName === f.file_name}
                              onChange={() => setSelectedFileName(f.file_name)}
                            />
                          </td>
                          <td>{f.parsed!.date}</td>
                          <td>{f.parsed!.time}</td>
                          <td className="text-uppercase">{f.parsed!.env}</td>
                          <td>{formatBytes(f.file_size)}</td>
                          <td>{new Date(f.last_modified).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card>
            <Card.Body>
              <Card.Title>Restore</Card.Title>
              <Alert variant="warning">
                Restoring will <strong>replace live collections</strong> using a safe swap. A
                pre-restore copy is kept for rollback.
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>Restore scope</Form.Label>
                <div className="d-flex flex-column gap-2">
                  <Form.Check
                    type="radio"
                    name="restoreMode"
                    label="All collections"
                    checked={restoreMode === "all"}
                    onChange={() => setRestoreMode("all")}
                  />
                  <Form.Check
                    type="radio"
                    name="restoreMode"
                    label="Selected collections"
                    checked={restoreMode === "selected"}
                    onChange={() => setRestoreMode("selected")}
                  />
                </div>
              </Form.Group>

              {restoreMode === "selected" && (
                <Form.Group className="mb-3">
                  <Form.Label>Collections</Form.Label>
                  {loadingCollections ? (
                    <div className="py-2">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading collections...
                    </div>
                  ) : collections.length === 0 ? (
                    <Alert variant="info" className="mb-0">
                      No collections returned.
                    </Alert>
                  ) : (
                    <div className="backup-files-collections">
                      {collections.map((c) => (
                        <Form.Check
                          key={c}
                          type="checkbox"
                          label={c}
                          checked={!!selectedCollections[c]}
                          onChange={() => handleToggleCollection(c)}
                        />
                      ))}
                    </div>
                  )}
                </Form.Group>
              )}

              <AdminButton
                intent="primary"
                onClick={handleTriggerRestore}
                disabled={triggeringRestore}
              >
                {triggeringRestore ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Scheduling...
                  </>
                ) : (
                  "Restore"
                )}
              </AdminButton>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default BackupFilesPage;

